class PngExporter {
    static WORKER_STR = function (href: string) {
        const initLodepng = () => {
            return new Promise((resolve) => {
                (self as any).importScripts(`${href}static/lib/lodepng/lodepng.js`);
                resolve((self as any).lodepng({
                    locateFile: () => `${href}static/lib/lodepng/lodepng.wasm`
                }));
            });
        };

        const compress = (lodepng: any, words: any[], width: number, height: number, flipVertical: boolean): Uint8Array => {
            const resultDataPtrPtr = lodepng._malloc(4);
            const resultSizePtr = lodepng._malloc(4);
            const imageData = lodepng._malloc(width * height * 4);

            // copy pixels into wasm memory
            if (flipVertical) {
                // Flip vertically (for Edge and other browsers)
                for (let y = 0; y < height; ++y) {
                    let soff = y * width;
                    let doff = imageData / 4 + (height - 1 - y) * width;
                    for (let x = 0; x < width; ++x) {
                        lodepng.HEAPU32[doff++] = words[soff++];
                    }
                }
            } else {
                // No flip (for Chrome - data is already in correct orientation)
                for (let y = 0; y < height; ++y) {
                    let soff = y * width;
                    let doff = imageData / 4 + y * width;
                    for (let x = 0; x < width; ++x) {
                        lodepng.HEAPU32[doff++] = words[soff++];
                    }
                }
            }

            // invoke compress
            lodepng._lodepng_encode32(resultDataPtrPtr, resultSizePtr, imageData, width, height);

            // read results
            const result = lodepng.HEAPU8.slice(lodepng.HEAPU32[resultDataPtrPtr / 4], lodepng.HEAPU32[resultDataPtrPtr / 4] + lodepng.HEAPU32[resultSizePtr / 4]);

            lodepng._free(resultDataPtrPtr);
            lodepng._free(resultSizePtr);
            lodepng._free(imageData);

            return result;
        };

        const main = async () => {
            const lodepng = await initLodepng();

            self.onmessage = (message) => {
                const data = message.data;

                // compress
                const result = compress(lodepng, data.words, data.width, data.height, data.flipVertical || false);

                // return with request ID to match responses
                self.postMessage({ 
                    result: result,
                    requestId: data.requestId 
                }, undefined, [result.buffer]);
            };
        };

        main();
    }.toString();

    worker: Worker;
    private pendingRequests: Map<number, (result: Uint8Array) => void>;
    private nextRequestId: number;

    constructor() {
        this.pendingRequests = new Map();
        this.nextRequestId = 0;

        const workerBlob = new Blob([`(${PngExporter.WORKER_STR})('${window.location.href.split('?')[0]}')\n\n`], {
            type: 'application/javascript'
        });
        this.worker = new Worker(URL.createObjectURL(workerBlob));
        this.worker.addEventListener('message', (message) => {
            const { result, requestId } = message.data;
            const resolve = this.pendingRequests.get(requestId);
            if (resolve) {
                this.pendingRequests.delete(requestId);
                resolve(result);
            }
        });
    }

    // download the data uri
    _downloadFile(filename: string, data: any) {
        const blob = new Blob([data], { type: 'octet/stream' });
        const url = window.URL.createObjectURL(blob);

        const el = document.createElement('a');
        el.download = filename;
        el.href = url;
        el.click();

        window.URL.revokeObjectURL(url);
    }

    // Detect if we're running in Chrome
    // Chrome reads texture data top-down (correct orientation, no flip needed)
    // Edge reads texture data bottom-up (needs vertical flip)
    // NOTE: With flipY: true on render target, we may need to always flip
    private needsVerticalFlip(): boolean {
        // Check for Chrome user agent (but not Edge, which also has Chrome in UA)
        const ua = navigator.userAgent;
        const isChrome = /Chrome/.test(ua) && !/Edg/.test(ua);
        // With flipY: true on screenshot render targets, we need to flip to correct orientation
        // Always flip to ensure correct orientation regardless of browser
        return true;
    }

    private async processExport(words: Uint32Array, width: number, height: number): Promise<Uint8Array> {
        const requestId = this.nextRequestId++;
        const flipVertical = this.needsVerticalFlip();
        
        return new Promise((resolve, reject) => {
            // Store the resolve callback with the request ID
            this.pendingRequests.set(requestId, resolve);
            
            // Set a timeout to prevent hanging forever
            setTimeout(() => {
                if (this.pendingRequests.has(requestId)) {
                    this.pendingRequests.delete(requestId);
                    reject(new Error('PNG export timeout'));
                }
            }, 30000); // 30 second timeout
            
            // Send message to worker with request ID
            try {
                this.worker.postMessage({
                    words: words,
                    width: width,
                    height: height,
                    flipVertical: flipVertical,
                    requestId: requestId
                }, [words.buffer]);
            } catch (error) {
                this.pendingRequests.delete(requestId);
                reject(error);
            }
        });
    }

    async export(filename: string, words: Uint32Array, width: number, height: number): Promise<void> {
        const data = await this.processExport(words, width, height);
        this._downloadFile(filename, data);
    }
    
    // Export to a specific directory using File System Access API (avoids Chrome's (1), (2) numbering)
    async exportToDirectory(directoryHandle: any, filename: string, words: Uint32Array, width: number, height: number): Promise<void> {
        const data = await this.processExport(words, width, height);
        
        // Create or get file handle (will overwrite if exists)
        const fileHandle = await directoryHandle.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(data);
        await writable.close();
    }
}

export {
    PngExporter
};
